import { OlympusClient } from '@/OlympusClient'
import { convertToPathObject } from '@/utils/structure'

import defaultState from './state'

const multiLocationActions = {
  locations: {
    loadByBrandId: async ({ commit }, brandId) => {
      const { results } = await OlympusClient.locations.getLocations({
        query: {
          brandId,
          limit: 100,
        },
      })

      for (const location of results) {
        commit('SET_LOCATION', location)
      }
    },
    setAll: ({ commit }, locations) => {
      commit('SET_ACTIVE_LOCATION', null)
      commit('SET_LOCATIONS', locations)
    },
    setDefaultState: ({ commit }) => {
      commit('SET_DEFAULTS', defaultState)
    },
  },
}

const singleLocationActions = {
  location: {
    load: async ({ commit }, locationId) => {
      const location = await OlympusClient.locations.location.getLocation({
        params: {
          locationId,
        },
      })

      commit('SET_LOCATION', location)
    },

    setActive: async ({ commit }, locationId) => {
      commit('SET_ACTIVE_LOCATION', locationId)
    },

    /**
     * WARNING - This is dangerous.  Check with Tucker before using.
     * Sets location from outside store module
     *
     * TECH_DEBT: https://www.notion.so/owner-com/Multiple-stores-in-hermes-6dfa54e0c45848b8b84b8295e6282030?pvs=4
     * @deprecated This should not be used.  It only exists to sync from the old store module and the new one.  Will be removed when old module is no longer used.
     * @param {object} Vuex ignore
     * @param {Function} Vuex.commit ignore
     * @param {object} location Owner Location
     */
    setLocation: async ({ commit }, location) => {
      commit('SET_LOCATION', location)
    },

    update: {
      // can just call global updateLocation action
      address: async ({ commit }, { locationId, newAddress }) => {
        const { id, address } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              address: newAddress,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_ADDRESS', { id, update: address })
      },
      // can just call global updateLocation action
      analytics: async ({ commit }, { locationId, newAnalytics }) => {
        const location = await OlympusClient.locations.location.updateLocation({
          body: {
            analytics: newAnalytics,
          },
          params: {
            locationId,
          },
        })

        commit('SET_LOCATION', location)
      },
      cateringSettings: async (
        { commit },
        {
          locationId,
          maximumOrderWindowDays,
          minimumLeadTimeHours,
          reviewCateringOrders,
        }
      ) => {
        const location =
          await OlympusClient.locations.location.updateCateringSettings({
            body: {
              maximumOrderWindowDays,
              minimumLeadTimeHours,
              reviewCateringOrders,
            },
            params: { locationId },
          })

        commit('SET_LOCATION', location)
      },
      // can just call global updateLocation action
      contact: async ({ commit }, { locationId, newContact }) => {
        const { id, contact } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              contact: newContact,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_CONTACT', { id, update: contact })
      },
      // features/* is the only action ever called directly so can just use global updateLocation
      features: {
        '*': async ({ commit }, { locationId, newFeatures }) => {
          const { id, features } =
            await OlympusClient.locations.location.updateLocation({
              body: {
                features: newFeatures,
              },
              params: {
                locationId,
              },
            })

          commit('UPDATE_LOCATION_FEATURES', { id, update: features })
        },
        automations: async ({ commit }, { locationId, newAutomations }) => {
          const {
            id,
            features: { automations },
          } = await OlympusClient.locations.location({
            body: {
              features: {
                automations: newAutomations,
              },
            },
            params: {
              locationId,
            },
          })

          commit('UPDATE_LOCATION_AUTOMATIONS', { id, update: automations })
        },
      },
      // can just call global updateLocation action
      funnel: async ({ commit }, { locationId, newFunnel }) => {
        const { id, funnel } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              funnel: newFunnel,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_FUNNEL', { id, update: funnel })
      },
      guestBearingFee: async ({ commit }, { locationId, online, store }) => {
        const response =
          await OlympusClient.locations.location.updateGuestBearing({
            body: {
              online,
              store,
            },
            params: { locationId },
          })

        commit('SET_LOCATION', {
          id: locationId,
          ownerFeeSettings: response.settings,
        })
      },
      // can just call global updateLocation action
      jobs: async ({ commit }, { locationId, newJobs }) => {
        const { id, jobs } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              jobs: newJobs,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_JOBS', { id, update: jobs })
      },
      link: async ({ commit, getters }, { locationId, update }) => {
        const targetLinks = getters['location/funnel/links/*'](locationId)

        if (targetLinks) {
          const updatedLinks = { ...targetLinks, ...update }

          const location =
            await OlympusClient.locations.location.funnel.setLinks({
              body: updatedLinks,
              params: {
                locationId,
              },
            })

          commit('SET_LOCATION', location)
        }
      },
      listingSyncTime: async ({ commit }, { id, update }) => {
        commit('UPDATE_LOCATION_LISTING_UPDATE_TIME', {
          id,
          update,
        })
      },
      // can just call global updateLocation action
      listingsData: async ({ commit }, { locationId, newListingsData }) => {
        const { id, listingsData } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              listingsData: newListingsData,
            },
            params: {
              locationId,
            },
          })
        commit('UPDATE_LOCATION_LISTINGS_DATA', { id, update: listingsData })
      },
      // can just call global updateLocation action
      messaging: async ({ commit }, { locationId, newMessaging }) => {
        const { id, messaging } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              messaging: newMessaging,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_MESSAGING', { id, update: messaging })
      },
      // can just call global updateLocation action
      name: async ({ commit }, { locationId, newName }) => {
        const { id, name } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              name: newName,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_NAME', { id, update: name })
      },
      // can just call global updateLocation action
      orderTypes: async ({ commit }, { locationId, newOrderTypes }) => {
        const { id, orderTypes } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              orderTypes: newOrderTypes,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_ORDER-TYPES', { id, update: orderTypes })
      },
      otter: async ({ commit }, { locationId, otterStoreId }) => {
        const location = await OlympusClient.locations.location.linkOtterStore({
          body: {
            otterStoreId,
          },
          params: {
            locationId,
          },
        })
        commit('SET_LOCATION', location)
      },
      ownerFeeSettings: async ({ commit }, { locationId, online, store }) => {
        const response =
          await OlympusClient.locations.location.updateOwnerFeeSettings({
            body: {
              online,
              store,
            },
            params: { locationId },
          })

        commit('SET_LOCATION', {
          id: locationId,
          ownerFeeSettings: response.settings,
        })
      },
      ownerPos: async ({ commit }, { locationId }) => {
        const location =
          await OlympusClient.locations.location.configureOwnerPos({
            body: {},
            params: { locationId },
          })
        commit('SET_LOCATION', location)
      },
      payments: async ({ commit }, { locationId, newPayments }) => {
        const { id, payments } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              payments: newPayments,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_PAYMENTS', { id, update: payments })
      },
      posFeatures: async ({ commit }, { locationId, updatedFeatures }) => {
        const location =
          await OlympusClient.locations.location.updatePosFeatures({
            body: updatedFeatures,
            params: { locationId },
          })
        commit('SET_LOCATION', location)
      },
      // can just call global updateLocation action
      preparation: async ({ commit }, { locationId, newPreparation }) => {
        const { id, preparation } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              preparation: newPreparation,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_PREPARATION', { id, update: preparation })
      },
      // can just call global updateLocation action
      primaryLocation: async (
        { commit },
        { locationId, newPrimaryLocation }
      ) => {
        const location = await OlympusClient.locations.location.updateLocation({
          body: {
            primaryLocation: newPrimaryLocation,
          },
          params: { locationId },
        })
        commit('SET_LOCATION', location)
      },
      removePos: async ({ commit }, { locationId }) => {
        const location =
          await OlympusClient.locations.location.removePosIntegration({
            params: { locationId },
          })
        commit('SET_LOCATION', location)
      },
      removeStripe: async ({ commit, getters }, { locationId }) => {
        const targetLocation = getters['location/byId'](locationId)

        const processingUpdate = {
          bearer: targetLocation?.processing?.bearer,
          stripe: {
            id: null,
          },
          type: 'stripe',
        }

        const location = await OlympusClient.locations.location.updateLocation({
          body: {
            processing: processingUpdate,
          },
          params: {
            locationId,
          },
        })

        commit('SET_LOCATION', location)
      },
      // can just call global updateLocation action
      sendFormsTo: async ({ commit }, { locationId, newSendFormsTo }) => {
        const { id, sendFormsTo } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              sendFormsTo: newSendFormsTo,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_SEND_FORMS_TO', { id, update: sendFormsTo })
      },
      // can just call global updateLocation action
      slug: async ({ commit }, { locationId, newSlug }) => {
        const { id, slug } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              slug: newSlug,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_SLUG', { id, update: slug })
      },
      square: async ({ commit }, { locationId, updatedSquareId }) => {
        const location =
          await OlympusClient.locations.location.linkSquareLocation({
            body: {
              squareLocationId: updatedSquareId,
            },
            params: {
              locationId,
            },
          })
        commit('SET_LOCATION', location)
      },
      // can just call global updateLocation action
      theme: async ({ commit }, { locationId, newTheme }) => {
        const { id, theme } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              theme: newTheme,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_THEME', { id, update: theme })
      },
      // can just call global updateLocation action
      throttle: async ({ commit }, { locationId, newThrottle }) => {
        const { id, throttle } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              throttle: newThrottle,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_THROTTLE', { id, update: throttle })
      },
      // can just call global updateLocation action
      timezone: async ({ commit }, { locationId, newTimezone }) => {
        const { id, timezone } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              timezone: newTimezone,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_TIMEZONE', { id, update: timezone })
      },
      // can just call global updateLocation action
      woflow: async ({ commit }, { locationId, newWoflow }) => {
        const { id, woflow } =
          await OlympusClient.locations.location.updateLocation({
            body: {
              woflow: newWoflow,
            },
            params: {
              locationId,
            },
          })

        commit('UPDATE_LOCATION_WOFLOW', { id, update: woflow })
      },
    },
  },
}

export default {
  ...convertToPathObject(multiLocationActions),
  ...convertToPathObject(singleLocationActions),
}
